import {
  Analysis,
  DashboardConfigurationDependencies,
  Granularity,
} from '../../../../dashboard-config/dashboard-config.model';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import {
  DashboardDistrictSingleSelectFilter,
  DashboardRemoteStateSingleSelectFilter,
  DashboardStateSingleSelectFilter,
  DashboardStatesMultiSelectFilter,
  DashboardSubMarketSingleSelectFilter,
  DashboardSubMarketsMultiSelectFilter,
  DashboardWzClassesMultiSelectFilter,
  DashboardWzClassSingleSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';

export const COMPARISON_FOUR_GRID_SHELL_ID = 'comparison-four-grid' as const;

export type DashboardComparisonFourGridShell =
  | 'states'
  | 'wzClassStates'
  | 'districtSubMarkets'
  | 'subMarkets'
  | 'wzClasses'
  | 'districtWzClasses';

export interface DashboardComparisonFourGridShellProfile {
  id: typeof COMPARISON_FOUR_GRID_SHELL_ID;
  analysisId: Analysis;
  granularityId: Granularity;
  buildGranularityFilters: (
    deps: DashboardConfigurationDependencies,
  ) => DashboardFilter<any>[];
  headingCompareLabel: string;
}

export const dashboardComparisonFourGridShellProfiles: Record<DashboardComparisonFourGridShell, DashboardComparisonFourGridShellProfile> = {
  states: {
    id: COMPARISON_FOUR_GRID_SHELL_ID,
    analysisId: Analysis.Comparison,
    granularityId: Granularity.States,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
        allInitiallySelectedExceptValue: ['-1'],
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        showInFilterComponent: false,
        ignoreProductSetting: true,
        autoSelectByIndex: 0,
        showLabel: false,
      }),
    ],
    headingCompareLabel: '4-TEILMÄRKTE-VERGLEICH:',
  },
  wzClassStates: {
    id: COMPARISON_FOUR_GRID_SHELL_ID,
    analysisId: Analysis.Comparison,
    granularityId: Granularity.WzClassStates,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
        allInitiallySelectedExceptValue: ['-1'],
      }),
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {
        showInFilterComponent: true,
        allInitiallySelectedExceptValue: deps.sumSubMarketIds
      }),
      new DashboardWzClassSingleSelectFilter(deps.localDatasetsService, {
        showInFilterComponent: false,
        autoSelectByIndex: 0,
        showLabel: false,
        parentFilterName: 'sub_markets'
      })
    ],
    headingCompareLabel: '4-WZ KLASSE-VERGLEICH:',
  },
  subMarkets: {
    id: COMPARISON_FOUR_GRID_SHELL_ID,
    analysisId: Analysis.Comparison,
    granularityId: Granularity.SubMarkets,
    buildGranularityFilters: (deps) => [
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelectedExceptValue: deps.sumSubMarketIds,
      }),
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {
        showInFilterComponent: false,
        ignoreProductSetting: true,
        autoSelectByIndex: 0,
        showLabel: false,
      }),
    ],
    headingCompareLabel: '4-LÄNDER-VERGLEICH:',
  },
  districtSubMarkets: {
    id: COMPARISON_FOUR_GRID_SHELL_ID,
    analysisId: Analysis.Comparison,
    granularityId: Granularity.DistrictSubMarkets,
    buildGranularityFilters: (deps) => [
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, { allInitiallySelectedExceptValue: deps.sumSubMarketIds }),
      // The district-level dataset has no Σ Germany rows, so the grids compare districts of a single state
      new DashboardRemoteStateSingleSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
        autoDisplayFirst: true,
      }),
      new DashboardDistrictSingleSelectFilter(deps.localDatasetsService, {
        showInFilterComponent: false,
        ignoreProductSetting: true,
        autoSelectByIndex: 0,
        showLabel: false,
      }),
    ],
    headingCompareLabel: '4-KREISE-VERGLEICH:',
  },
  wzClasses: {
    id: COMPARISON_FOUR_GRID_SHELL_ID,
    analysisId: Analysis.Comparison,
    granularityId: Granularity.WzClasses,
    buildGranularityFilters: (deps) => [
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        excluded: deps.sumSubMarketIds,
      }),
      new DashboardWzClassesMultiSelectFilter(deps.localDatasetsService, {}),
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {
        showInFilterComponent: false,
        ignoreProductSetting: true,
        autoSelectByIndex: 0,
        showLabel: false,
      }),
    ],
    headingCompareLabel: '4-LÄNDER-VERGLEICH:',
  },
  districtWzClasses: {
    id: COMPARISON_FOUR_GRID_SHELL_ID,
    analysisId: Analysis.Comparison,
    granularityId: Granularity.DistrictWzClasses,
    buildGranularityFilters: (deps) => [
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        excluded: deps.sumSubMarketIds,
      }),
      new DashboardWzClassesMultiSelectFilter(deps.localDatasetsService, {}),
      // As `districtSubMarkets`: the district-level dataset has no Σ Germany rows, so the grids compare districts of a single state
      new DashboardRemoteStateSingleSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
        autoDisplayFirst: true,
      }),
      new DashboardDistrictSingleSelectFilter(deps.localDatasetsService, {
        showInFilterComponent: false,
        ignoreProductSetting: true,
        autoSelectByIndex: 0,
        showLabel: false,
      }),
    ],
    headingCompareLabel: '4-KREISE-VERGLEICH:',
  },
};
